import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Play, Pause, RotateCcw } from 'lucide-react';
import SEO from '../components/SEO';

const TIMER_OPTIONS = [
  { label: '3 min', seconds: 180 },
  { label: '5 min', seconds: 300 },
  { label: '10 min', seconds: 600 },
  { label: '20 min', seconds: 1200 },
];

const steps = [
  {
    number: 1,
    title: 'Arrive',
    instruction: 'Sit comfortably. Close your eyes. Take three slow breaths. You are not going anywhere. You are already here.',
  },
  {
    number: 2,
    title: 'Notice',
    instruction: 'Without trying to change anything, notice what is present. Sounds. Sensations. The feeling of being alive. Just notice.',
  },
  {
    number: 3,
    title: 'Release',
    instruction: 'Whatever you are holding, let it go. Not forever. Just for now. Thoughts will come. Let them pass like clouds.',
  },
  {
    number: 4,
    title: 'Rest',
    instruction: 'Rest in the space between thoughts. This is not emptiness. It is fullness without labels. Stay here as long as feels natural.',
  },
  {
    number: 5,
    title: 'Return',
    instruction: 'Gently open your eyes. Carry this quiet with you. The practice is not separate from life. It is life, noticed.',
  },
];

const layers = [
  {
    title: 'Neurological',
    text: 'Slows the default mode network (the ego\'s narrator), activates the parasympathetic nervous system, reduces cortisol.',
  },
  {
    title: 'Psychological',
    text: 'Creates space between stimulus and response. You stop reacting and start choosing. Identity loosens its grip.',
  },
  {
    title: 'Philosophical',
    text: 'Direct experience of Pattern 6: the present moment as the only point of entry. Not theory. Practice.',
  },
  {
    title: 'Spiritual',
    text: 'When the ego quiets, what remains is awareness without boundaries. This is Pattern 7. This is what every tradition points to.',
  },
];

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function Practice() {
  const [timerDuration, setTimerDuration] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const intervalRef = useRef(null);

  const stopTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setRunning(false);
  }, []);

  const startTimer = useCallback((seconds) => {
    stopTimer();
    setTimerDuration(seconds);
    setTimeLeft(seconds);
    setDone(false);
    setRunning(true);
  }, [stopTimer]);

  useEffect(() => {
    if (!running) return;

    intervalRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          stopTimer();
          setDone(true);
          // gentle vibration if supported
          if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [running, stopTimer]);

  const togglePause = () => {
    if (running) {
      stopTimer();
    } else if (timeLeft > 0) {
      setRunning(true);
    }
  };

  const reset = () => {
    stopTimer();
    setTimerDuration(null);
    setTimeLeft(0);
    setDone(false);
  };

  const progress = timerDuration ? ((timerDuration - timeLeft) / timerDuration) * 100 : 0;

  return (
    <div className="min-h-screen pt-24 pb-20 px-6">
      <SEO path="/practice" />
      <div className="max-w-2xl mx-auto">
        <h1 className="font-serif text-3xl md:text-4xl text-warm text-center mb-4">
          Daily Practice
        </h1>
        <p className="text-warm-100/50 text-center mb-16 font-serif italic">
          Five steps. Any amount of time. No expertise required.
        </p>

        {/* Steps */}
        <div className="space-y-10 mb-20">
          {steps.map((step) => (
            <div key={step.number} className="flex gap-5">
              <div className="flex-shrink-0 w-10 h-10 rounded-full border border-gold/20 flex items-center justify-center">
                <span className="text-gold/60 font-serif text-sm">{step.number}</span>
              </div>
              <div className="pt-1.5">
                <h2 className="font-serif text-lg text-warm mb-2">{step.title}</h2>
                <p className="text-warm-100/60 leading-relaxed">{step.instruction}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Timer */}
        <div className="border border-dark-200/30 rounded-lg p-8 mb-20">
          <h2 className="font-serif text-xl text-warm text-center mb-6">Meditation Timer</h2>

          {!timerDuration ? (
            <div className="flex flex-wrap justify-center gap-3">
              {TIMER_OPTIONS.map(opt => (
                <button
                  key={opt.seconds}
                  onClick={() => startTimer(opt.seconds)}
                  className="px-6 py-2.5 bg-gold/10 border border-gold/20 text-gold/80 rounded-sm hover:bg-gold/20 hover:text-gold transition-all text-sm tracking-wide"
                >
                  {opt.label}
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center">
              {/* Progress bar */}
              <div className="w-full h-0.5 bg-dark-200 rounded-full mb-8 overflow-hidden">
                <div
                  className="h-full bg-gold/50 transition-all duration-1000 ease-linear"
                  style={{ width: `${progress}%` }}
                />
              </div>

              <p className="text-5xl font-serif text-warm mb-8 tabular-nums">
                {formatTime(timeLeft)}
              </p>

              {done && (
                <p className="text-gold/70 font-serif italic mb-6 animate-fade-in">
                  Time is complete. Return gently.
                </p>
              )}

              <div className="flex justify-center gap-3">
                {!done && (
                  <button
                    onClick={togglePause}
                    className="p-3 bg-gold/10 border border-gold/20 text-gold/80 rounded-lg hover:bg-gold/20 transition-colors"
                  >
                    {running ? <Pause size={20} /> : <Play size={20} />}
                  </button>
                )}
                <button
                  onClick={reset}
                  className="p-3 bg-dark-200/30 border border-dark-200/50 text-warm-100/50 rounded-lg hover:text-warm-100/70 transition-colors"
                >
                  <RotateCcw size={20} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Why it works */}
        <div>
          <h2 className="font-serif text-2xl text-warm text-center mb-10">
            Why It Works
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {layers.map((layer) => (
              <div key={layer.title} className="border border-dark-200/20 rounded-lg p-6">
                <h3 className="text-gold/70 text-sm tracking-wide uppercase mb-3">
                  {layer.title}
                </h3>
                <p className="text-warm-100/60 leading-relaxed text-sm">
                  {layer.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
